import React, { useContext } from "react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import { UserIdentityFlowAction } from "@/services/UserIdentitiesService";

// Components
import IdentityErrorHelper from "@/screen/Identities/IdentityErrorHelper";
import IdentityDocumentUpload from "@/screen/Identities/IdentityDocumentUpload";
import IdentityPersonalDataInputs from "@/screen/Identities/IdentityPersonalDataInputs/IdentityPersonalDataInputs";
import IdentityAddressInputs from "@/screen/Identities/IdentityAddressInputs/IdentityAddressInputs";
import IdentityResponsibleDocumentInputs from "@/screen/Identities/IdentityResponsibleDocumentInputs/IdentityResponsibleDocumentInputs";
import IdentityFinanceInputs from "@/screen/Identities/IdentityFinanceInputs/IdentityFinanceInputs";
import IdentityFiscalInputs from "@/screen/Identities/IdentityFiscalInputs/IdentityFiscalInputs";
import IdentityKycInputs from "@/screen/Identities/IdentityKycInputs/IdentityKycInputs";
import ModalSaveSuccess from "@/screen/Identities/ModalSaveSuccess";
import ModalPromoteToMain from "@/screen/Identities/ModalPromoteToMain";
import TicketSupport from "@/components/TicketSupport/TicketSupport";
import useTicketSupport from "@/hooks/useTicketSupport";
import { ScreenStepType } from "@/components/TicketSupport/TicketSupportConstants";

// Hook orquestador
import { useIdentityFormController } from "@/screen/Identities/IdentityForm/useIdentityFormController";

// Styles
import { HelperStyle, Cont, MainCont, Title, TitleSubtitleAssistRow, BarCont, BgBar, FormContainer, FlowActionContainer, FlowInfoBanner, ServerWarningBanner, ButtonsRow, SaveActionsInfo, PendingFieldsNotice } from "@/screen/Identities/Styles/IdentityFormStyles";

const IdentityForm: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);

    const
    {
        mode,
        userIdentityId,
        formData,
        isLoading,
        isSaving,
        savingAction,
        showSaveSuccessModal,
        closeSaveSuccessModal,
        isFormLocked,
        isIdTypeLocked,
        isEmailLocked,
        canRenewIdentityDocument,
        isMissingIdentityDocuments,
        hasStoredIdentityDocuments,
        isStoredDocumentExpired,
        isStoredIdExpirationMissing,
        requiresMinorGuardianDocument,
        hasBirthdayBusinessError,
        hasIdExpirationBusinessError,
        validationErrors,
        serverWarning,
        highlightPendingManualFields,
        validation,
        identityCatalogs,
        countriesCatalog,
        catalogsLoading,
        catalogsError,
        professionCatalogsError,
        identityStatus,
        flowRequestLoading,
        flowRequestError,
        flowRequestResultMessage,
        isFormVisible,
        canShowFinalizeAction,
        isEditActiveIdentity,
        canFinalizeSignUp,
        isFormValid,
        handleCancel,
        handleSubmit,
        handleFinalizeSignUp,
        handleSubmitFlowRequestAction,
        handleReactivateOwnIdentity,
        flowPanelRef,
        canOfferTransferAsMain,
        handleRequestTransferAsMain,
        showAcceptTransferPanel,
        canAcceptTransferAsMain,
        acceptTransferOptionsLoading,
        acceptTransferSubmitting,
        acceptTransferError,
        acceptTransferSuccessMessage,
        acceptTransferIdentityFullName,
        handleRequestAcceptTransferAsMain,
        handleAcceptTransfer,
        promoteToMainFullName,
        showPromoteToMainModal,
        promoteToMainError,
        promoteToMainSubmitting,
        handleConfirmPromoteToMain,
        handleCancelPromoteToMain,
        handleChange,
        handleKycActivityAdd,
        handleKycActivityRemove,
        handleKycActivityChange,
        setIsKycBlocked,
        handleBirthdayChange,
        handleIdExpirationChange,
        handlePermanentIdExpirationToggle,
        handleProofOfMinorGuardianChange,
        handleProofOfIncomeChange,
        handleUseSinglePhoneToggle,
        handlePostalAddressChange,
        handleFiscalAddressChange,
        handleSameAddressToggle,
        errorsRef,
        handleDocumentProcessed,
        handleDocumentError,
        handleDocumentAttachmentErrors,
        handleFinanceAttachmentErrors,
        handleAddressCascadeLoadingChange,
        handleDocumentPreviewLoadingChange,
        handleServerWarning,
        handleDniConfirmed,
        handleFlowResolved,
        setIsFiscalBlocked,
        getTitle,
        getSubtitle,
    } = useIdentityFormController();

    const ticketSupport = useTicketSupport(
    {
        component: "IdentityForm",
        screenName: ScreenStepType.FormularioAltas,
        formVal: formData,
    });

    const existingPersonName = identityStatus?.existingPersonName;

    return(
        <DocumentTitle title={`IronIA - ${getTitle()}`}>
            <React.Fragment>
                <HelperStyle />
                <Cont theme={themeContext} className="identity-form">
                    <ThemeProvider theme={themeContext}>
                        <MainCont>
                            <Title>
                                <h1>{getTitle()}</h1>
                                <TitleSubtitleAssistRow>
                                    <h2>{getSubtitle()}</h2>
                                    <div className="identity-form-title-assist-trigger">
                                        <TicketSupport {...ticketSupport} />
                                    </div>
                                </TitleSubtitleAssistRow>
                            </Title>
                            <BarCont>
                                <BgBar />
                            </BarCont>
                            {isLoading ?
                            (
                                <FormContainer theme={themeContext}>
                                    <div style={{textAlign: "center", padding: "40px"}}>
                                        <Spinner size={SpinnerSize.large} />
                                    </div>
                                </FormContainer>
                            ) :
                            (
                                    <React.Fragment>
                                    {/* Errores bloqueantes del servidor */}
                                    <IdentityErrorHelper errors={validationErrors} errorsRef={errorsRef} />
                                    {/* Aviso no bloqueante del servidor (p. ej. EXISTS_ACTIVE_IN_USER) */}
                                    {serverWarning && !showAcceptTransferPanel &&
                                    (
                                        <ServerWarningBanner role="status">
                                            {serverWarning}
                                        </ServerWarningBanner>
                                    )}
                                    {/* Cesión aceptada como secundario: el formulario ya es editable */}
                                    {acceptTransferSuccessMessage && !showAcceptTransferPanel &&
                                    (
                                        <FlowInfoBanner role="status">
                                            {acceptTransferSuccessMessage}
                                        </FlowInfoBanner>
                                    )}
                                    {/* Interviniente en TRANSFER (recibido por cesión aprobada y sin aceptar): solo se puede
                                        aceptar la cesión, así que se pinta este formulario en lugar del de datos. */}
                                    {showAcceptTransferPanel &&
                                    (
                                        <FlowActionContainer theme={themeContext}>
                                            <p>
                                                Has recibido este interviniente{acceptTransferIdentityFullName ? ` (${acceptTransferIdentityFullName})` : ""} por una cesión de titularidad.
                                            </p>
                                            <p className="flow-subtitle">
                                                Para poder operar con él (editar sus datos, firmar su KYC o usarlo en un contrato) primero tienes que aceptar la cesión.
                                            </p>
                                            {/* Cuenta con solo el principal vacío de nacimiento: se decide si el interviniente
                                                pasa a ser el principal o se añade como secundario. */}
                                            {canAcceptTransferAsMain ?
                                            (
                                                <React.Fragment>
                                                    <p className="flow-subtitle">
                                                        Tu cuenta todavía no tiene un interviniente principal con datos. Puedes aceptarlo como interviniente principal de tu cuenta: en ese caso el interviniente principal vacío que tienes ahora se eliminará y la decisión no se podrá deshacer. También puedes aceptarlo como interviniente secundario.
                                                    </p>
                                                    <ButtonsRow>
                                                        <DefaultButton className="button tiny-primary-button" onClick={handleRequestAcceptTransferAsMain} disabled={acceptTransferSubmitting}>
                                                            Aceptar como interviniente principal
                                                        </DefaultButton>
                                                        <DefaultButton className="button tiny-secondary-button" onClick={handleAcceptTransfer} disabled={acceptTransferSubmitting}>
                                                            {acceptTransferSubmitting ? "Procesando..." : "Aceptar como interviniente secundario"}
                                                        </DefaultButton>
                                                        <DefaultButton className="button tiny-secondary-button" onClick={handleCancel} disabled={acceptTransferSubmitting}>
                                                            Volver
                                                        </DefaultButton>
                                                    </ButtonsRow>
                                                </React.Fragment>
                                            ) :
                                            (
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-primary-button" onClick={handleAcceptTransfer} disabled={acceptTransferSubmitting || acceptTransferOptionsLoading}>
                                                        {acceptTransferSubmitting ? "Procesando..." : "Aceptar la cesión"}
                                                    </DefaultButton>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel} disabled={acceptTransferSubmitting}>
                                                        Volver
                                                    </DefaultButton>
                                                </ButtonsRow>
                                            )}
                                            {acceptTransferError &&
                                            (
                                                <ServerWarningBanner role="alert">
                                                    {acceptTransferError}
                                                </ServerWarningBanner>
                                            )}
                                        </FlowActionContainer>
                                    )}
                                    {/* ─── Sección de documento ──────────────────────────────────
                                        El hook useDocumentUpload dentro del componente gestiona
                                        el ciclo completo: upload → analyzeDocument → analyzeStatus.
                                        Cuando resuelve, llama a onFlowResolved para que el
                                        controller reaccione (cargar borrador, ocultar formulario…).
                                        ─────────────────────────────────────────────────────── */}
                                    {!showAcceptTransferPanel &&
                                    (
                                    <FormContainer theme={themeContext}>
                                        <IdentityDocumentUpload mode={mode}
                                            userIdentityId={userIdentityId}
                                            initialFrontPreview={typeof formData.idFront === "string" ? formData.idFront : null}
                                            initialBackPreview={typeof formData.idBack === "string" ? formData.idBack : null}
                                            initialIdDocument={formData.idDocument}
                                            canRenewIdentityDocument={canRenewIdentityDocument}
                                            isMissingIdentityDocuments={isMissingIdentityDocuments}
                                            isStoredDocumentExpired={isStoredDocumentExpired}
                                            isStoredIdExpirationMissing={isStoredIdExpirationMissing}
                                            hasStoredDocuments={hasStoredIdentityDocuments}
                                            onDocumentProcessed={handleDocumentProcessed}
                                            onServerWarning={handleServerWarning}
                                            onDniConfirmed={handleDniConfirmed}
                                            onError={handleDocumentError}
                                            onAttachmentErrors={handleDocumentAttachmentErrors}
                                            onPreviewLoadingChange={handleDocumentPreviewLoadingChange}
                                            onFlowResolved={handleFlowResolved}
                                        />
                                    </FormContainer>
                                    )}
                                    {/* ──────────────────────────────────────────────────────────────
                                        FLUJOS ESPECIALES (flow.action)
                                        Estos bloques son mutuamente excluyentes con el formulario
                                        estándar. Se renderizan cuando isFormVisible === false.
                                        La lógica la determina el backend a través de la propiedad flow.action.
                                        Con el formulario de aceptación de cesión (TRANSFER) no se pinta nada de esto.
                                        ────────────────────────────────────────────────────── */}
                                    {!showAcceptTransferPanel &&
                                    (
                                    <React.Fragment>
                                        {/* ResumeDraft: formulario visible + aviso de borrador reanudado */}
                                        {identityStatus?.action === UserIdentityFlowAction.ResumeDraft && isFormVisible &&
                                        (
                                            <FlowInfoBanner role="status">
                                                Se ha encontrado un borrador existente para este interviniente. Los datos se han cargado automáticamente.
                                            </FlowInfoBanner>
                                        )}
                                        {/* RequestAccess: el interviniente ya existe y pertenece a otro usuario */}
                                        {identityStatus?.action === UserIdentityFlowAction.RequestAccess && !isFormVisible &&
                                        (
                                            <FlowActionContainer theme={themeContext} ref={flowPanelRef} tabIndex={-1}>
                                                <p>
                                                    Este interviniente{existingPersonName ? ` (${existingPersonName})` : ""} ya existe en la plataforma y pertenece a otro usuario.
                                                </p>
                                                <p className="flow-subtitle">
                                                    Para operar con él debes solicitar acceso al titular. Una vez aprobada tu solicitud, el interviniente aparecerá en tu listado.
                                                </p>
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-primary-button" onClick={handleSubmitFlowRequestAction} disabled={flowRequestLoading}>
                                                        {flowRequestLoading ? "Procesando..." : "Solicitar acceso"}
                                                    </DefaultButton>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                        Volver
                                                    </DefaultButton>
                                                </ButtonsRow>
                                                {flowRequestResultMessage &&
                                                (
                                                    <FlowInfoBanner role="status">
                                                        {flowRequestResultMessage}
                                                    </FlowInfoBanner>
                                                )}
                                                {flowRequestError &&
                                                (
                                                    <ServerWarningBanner role="alert">
                                                        {flowRequestError}
                                                    </ServerWarningBanner>
                                                )}
                                            </FlowActionContainer>
                                        )}
                                        {/* InformationManualSignUpRequired: el alta debe tramitarse de forma manual */}
                                        {identityStatus?.action === UserIdentityFlowAction.InformationManualSignUpRequired && !isFormVisible &&
                                        (
                                            <FlowActionContainer theme={themeContext}>
                                                <p>
                                                    El alta de este interviniente{existingPersonName ? ` (${existingPersonName})` : ""} requiere tramitación manual.
                                                </p>
                                                <p className="flow-subtitle">
                                                    Por favor, contacta con nosotros a través del canal de soporte para completar el proceso. No es posible continuar de forma automática.
                                                </p>
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                        Volver
                                                    </DefaultButton>
                                                </ButtonsRow>
                                            </FlowActionContainer>
                                        )}
                                        {/* RequestOwnershipTransfer: el interviniente existe pero puede solicitar cesión de titularidad */}
                                        {identityStatus?.action === UserIdentityFlowAction.RequestOwnershipTransfer && !isFormVisible &&
                                        (
                                            <FlowActionContainer theme={themeContext} ref={flowPanelRef} tabIndex={-1}>
                                                <p>
                                                    Este interviniente{existingPersonName ? ` (${existingPersonName})` : ""} ya tiene titular registrado en la plataforma.
                                                </p>
                                                <p className="flow-subtitle">
                                                    Puedes solicitar una cesión de titularidad. El titular actual recibirá la solicitud y deberá aprobarla para que pases a ser el nuevo titular.
                                                </p>
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-primary-button" onClick={handleSubmitFlowRequestAction} disabled={flowRequestLoading}>
                                                        {flowRequestLoading ? "Procesando..." : "Solicitar cesión"}
                                                    </DefaultButton>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                        Volver
                                                    </DefaultButton>
                                                </ButtonsRow>
                                                {flowRequestResultMessage &&
                                                (
                                                    <FlowInfoBanner role="status">
                                                        {flowRequestResultMessage}
                                                    </FlowInfoBanner>
                                                )}
                                                {flowRequestError &&
                                                (
                                                    <ServerWarningBanner role="alert">
                                                        {flowRequestError}
                                                    </ServerWarningBanner>
                                                )}
                                            </FlowActionContainer>
                                        )}
                                        {/* AutomaticOwnershipTransfer: la cesión puede ejecutarse de forma automática */}
                                        {identityStatus?.action === UserIdentityFlowAction.AutomaticOwnershipTransfer && !isFormVisible &&
                                        (
                                            <FlowActionContainer theme={themeContext} ref={flowPanelRef} tabIndex={-1}>
                                                <p>
                                                    Este interviniente{existingPersonName ? ` (${existingPersonName})` : ""} puede transferirse automáticamente a tu cuenta.
                                                </p>
                                                <p className="flow-subtitle">
                                                    La cesión de titularidad se realizará de forma inmediata. Una vez completada, el interviniente aparecerá en tu listado como titular.
                                                </p>
                                                {/* Cuenta con solo el principal vacío de nacimiento: se decide antes de transferir si
                                                    el interviniente pasa a ser el principal o se añade como secundario. */}
                                                {canOfferTransferAsMain ?
                                                (
                                                    <React.Fragment>
                                                        <p className="flow-subtitle">
                                                            Tu cuenta todavía no tiene un interviniente principal con datos. Puedes hacer que este interviniente sea el principal de tu cuenta: en ese caso el interviniente principal vacío que tienes ahora se eliminará y la decisión no se podrá deshacer. También puedes añadirlo como interviniente secundario.
                                                        </p>
                                                        <ButtonsRow>
                                                            <DefaultButton className="button tiny-primary-button" onClick={handleRequestTransferAsMain} disabled={flowRequestLoading}>
                                                                {flowRequestLoading ? "Procesando..." : "Hacerlo el principal de mi cuenta"}
                                                            </DefaultButton>
                                                            <DefaultButton className="button tiny-secondary-button" onClick={handleSubmitFlowRequestAction} disabled={flowRequestLoading}>
                                                                Añadirlo como interviniente secundario
                                                            </DefaultButton>
                                                            <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                                Volver
                                                            </DefaultButton>
                                                        </ButtonsRow>
                                                    </React.Fragment>
                                                ) :
                                                (
                                                    <ButtonsRow>
                                                        <DefaultButton className="button tiny-primary-button" onClick={handleSubmitFlowRequestAction} disabled={flowRequestLoading}>
                                                            {flowRequestLoading ? "Procesando..." : "Transferir a mi cuenta"}
                                                        </DefaultButton>
                                                        <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                            Volver
                                                        </DefaultButton>
                                                    </ButtonsRow>
                                                )}
                                                {flowRequestResultMessage &&
                                                (
                                                    <FlowInfoBanner role="status">
                                                        {flowRequestResultMessage}
                                                    </FlowInfoBanner>
                                                )}
                                                {flowRequestError &&
                                                (
                                                    <ServerWarningBanner role="alert">
                                                        {flowRequestError}
                                                    </ServerWarningBanner>
                                                )}
                                            </FlowActionContainer>
                                        )}
                                        {/* ReactivateOwn: el documento es de un interviniente propio dado de baja y se puede recuperar */}
                                        {identityStatus?.action === UserIdentityFlowAction.ReactivateOwn && !isFormVisible &&
                                        (
                                            <FlowActionContainer theme={themeContext} ref={flowPanelRef} tabIndex={-1}>
                                                <p>
                                                    Este documento pertenece a un interviniente tuyo que diste de baja.
                                                </p>
                                                <p className="flow-subtitle">
                                                    Puedes recuperarlo y seguir con él.
                                                </p>
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-primary-button" onClick={handleReactivateOwnIdentity} disabled={flowRequestLoading}>
                                                        {flowRequestLoading ? "Procesando..." : "Recuperar interviniente"}
                                                    </DefaultButton>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                                        Volver
                                                    </DefaultButton>
                                                </ButtonsRow>
                                                {flowRequestError &&
                                                (
                                                    <ServerWarningBanner role="alert">
                                                        {flowRequestError}
                                                    </ServerWarningBanner>
                                                )}
                                            </FlowActionContainer>
                                        )}
                                        {/* FORMULARIO ESTÁNDAR Visible en: sin flujo especial, StartNewOnboarding, ResumeDraft y siempre en modo edición. */}
                                        {!isFormLocked && (isFormVisible || mode === "edit") &&
                                        (
                                            <React.Fragment>
                                                <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                    <IdentityPersonalDataInputs
                                                        formData={formData}
                                                        validation={validation}
                                                        highlightPendingManualFields={highlightPendingManualFields}
                                                        isFormLocked={isFormLocked}
                                                        isIdTypeLocked={isIdTypeLocked}
                                                        isEmailLocked={isEmailLocked}
                                                        hasBirthdayBusinessError={hasBirthdayBusinessError}
                                                        hasIdExpirationBusinessError={hasIdExpirationBusinessError}
                                                        onChange={handleChange}
                                                        onBirthdayChange={handleBirthdayChange}
                                                        onIdExpirationChange={handleIdExpirationChange}
                                                        onPermanentIdExpirationToggle={handlePermanentIdExpirationToggle}
                                                        onUseSinglePhoneToggle={handleUseSinglePhoneToggle}
                                                        countriesCatalog={countriesCatalog}
                                                    />
                                                </FormContainer>
                                                <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                    <IdentityAddressInputs
                                                        formData={formData}
                                                        validation={validation}
                                                        highlightPendingManualFields={highlightPendingManualFields}
                                                        isFormLocked={isFormLocked}
                                                        onPostalAddressChange={handlePostalAddressChange}
                                                        onFiscalAddressChange={handleFiscalAddressChange}
                                                        onSameAddressToggle={handleSameAddressToggle}
                                                        onCascadeLoadingChange={handleAddressCascadeLoadingChange}
                                                    />
                                                </FormContainer>
                                                {!formData.isMinor &&
                                                (
                                                    <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                        <IdentityFiscalInputs
                                                            formData={formData}
                                                            validation={validation}
                                                            highlightPendingManualFields={highlightPendingManualFields}
                                                            isFormLocked={isFormLocked}
                                                            onChange={handleChange}
                                                            onFiscalBlocked={setIsFiscalBlocked}
                                                            identityCatalogs={identityCatalogs}
                                                            catalogsLoading={catalogsLoading}
                                                            catalogsError={catalogsError}
                                                            countriesCatalog={countriesCatalog}
                                                            serverErrors={validationErrors}
                                                        />
                                                    </FormContainer>
                                                )}
                                                {/* El bloque de tutela también aparece cuando la fecha de nacimiento dice
                                                    que es menor, aunque se esté dando de alta por el flujo de adulto: el
                                                    backend lo exige por fecha, y sin renderizarlo no había forma de aportarlo. */}
                                                {(mode === "createMinor" || formData.isMinor || requiresMinorGuardianDocument) &&
                                                (
                                                    <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                        <IdentityResponsibleDocumentInputs
                                                            proofOfMinorGuardian={formData.proofOfMinorGuardian ?? null}
                                                            validation={validation}
                                                            highlightPendingManualFields={highlightPendingManualFields}
                                                            isFormLocked={isFormLocked}
                                                            onProofOfMinorGuardianChange={handleProofOfMinorGuardianChange}
                                                        />
                                                    </FormContainer>
                                                )}
                                                {!formData.isMinor &&
                                                (
                                                    <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                        <IdentityFinanceInputs
                                                            formData={formData}
                                                            validation={validation}
                                                            highlightPendingManualFields={highlightPendingManualFields}
                                                            isFormLocked={isFormLocked}
                                                            onChange={handleChange}
                                                            onProofOfIncomeChange={handleProofOfIncomeChange}
                                                            onProofOfIncomeErrors={handleFinanceAttachmentErrors}
                                                        />
                                                    </FormContainer>
                                                )}
                                                {/* Bloque KYC aplica a todos los intervinientes, también menores
                                                    (el backend exige su completitud al formalizar en ProcessAndSync). */}
                                                <FormContainer theme={themeContext} className={isFormLocked ? "locked" : ""}>
                                                    <IdentityKycInputs
                                                        formData={formData}
                                                        validation={validation}
                                                        highlightPendingManualFields={highlightPendingManualFields}
                                                        isFormLocked={isFormLocked}
                                                        onChange={handleChange}
                                                        onActivityAdd={handleKycActivityAdd}
                                                        onActivityRemove={handleKycActivityRemove}
                                                        onActivityChange={handleKycActivityChange}
                                                        onKycBlocked={setIsKycBlocked}
                                                        identityCatalogs={identityCatalogs}
                                                        catalogsLoading={catalogsLoading}
                                                        professionCatalogsError={professionCatalogsError}
                                                        serverErrors={validationErrors}
                                                    />
                                                </FormContainer>
                                                <SaveActionsInfo role="note">
                                                    {/* Sobre un interviniente ya activo no hay borrador que guardar: el
                                                        guardado va directo a validación y puede invalidar su KYC. */}
                                                    {isEditActiveIdentity &&
                                                    (
                                                        <p>
                                                            <strong>Guardar cambios:</strong> Los datos se envían a validación. Si el cambio afecta a tu documento KYC te avisaremos aquí mismo, porque tendrás que volver a firmarlo.
                                                        </p>
                                                    )}
                                                    {!isEditActiveIdentity &&
                                                    (
                                                        <p>
                                                            <strong>Guardar cambios:</strong> Permite guardar el formulario como borrador para que puedas seguir editándolo tantas veces como necesites.
                                                        </p>
                                                    )}
                                                    {canShowFinalizeAction &&
                                                    (
                                                        <p>
                                                            <strong>{mode === "edit" ? "Finalizar edición" : "Finalizar alta"}:</strong> {mode === "edit" ? "Cierra la edición" : "Cierra el alta"} y envía los datos para su comprobación y validación por nuestro equipo.
                                                        </p>
                                                    )}
                                                </SaveActionsInfo>
                                                <PendingFieldsNotice role="note">
                                                    Si alguno de los campos no se ha cumplimentado aparecerá en amarillo.
                                                </PendingFieldsNotice>
                                                <ButtonsRow>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={handleCancel} disabled={isSaving}>
                                                        Cancelar
                                                    </DefaultButton>
                                                    <DefaultButton className="button tiny-primary-button" onClick={handleSubmit} disabled={isSaving || !isFormValid()}>
                                                        {savingAction === "draft" && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                        Guardar cambios
                                                    </DefaultButton>
                                                    {canShowFinalizeAction &&
                                                    (
                                                        <DefaultButton className="button tiny-primary-button" onClick={handleFinalizeSignUp} disabled={isSaving || !canFinalizeSignUp}>
                                                            {savingAction === "finalize" && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                            {mode === "edit" ? "Finalizar edición" : "Finalizar alta"}
                                                        </DefaultButton>
                                                    )}
                                                </ButtonsRow>
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                    )}
                                    </React.Fragment>
                            )}
                        </MainCont>
                    </ThemeProvider>
                </Cont>
                <ModalSaveSuccess isOpen={showSaveSuccessModal} onClose={closeSaveSuccessModal} />
                <ModalPromoteToMain
                    isOpen={showPromoteToMainModal}
                    identityFullName={promoteToMainFullName}
                    errorMessage={promoteToMainError}
                    isSubmitting={promoteToMainSubmitting}
                    onConfirm={handleConfirmPromoteToMain}
                    onCancel={handleCancelPromoteToMain}
                />
            </React.Fragment>
        </DocumentTitle>
    );
};

export default IdentityForm;